import { format } from 'date-fns';
import _ from 'lodash';

import { DateHelper } from 'src/app/helpers/date.helper';
import {
  DeliveryType,
  MealPlanTransactionSignature,
  GratuitySettings,
  MealPlanType,
  PickupDeliveryFee,
  PickupDeliveryFeeType,
  SignatureStatus,
  Ticket,
  TicketSource,
  TicketTransaction
} from 'src/app/models';

export class TicketHelper {
  /**
   * POS-972 On-Screen Signature.
   * First bill-to-room/charge-account transaction still awaiting a signature/skip decision.
   * Shared by `checkout.page.ts` and `payments.page.ts` — previously duplicated in both.
   */
  static getPendingTransactionSignature(
    ticket: Ticket
  ): TicketTransaction | undefined {
    return ticket?.transactions?.find(
      (transaction) =>
        transaction.signature_required &&
        (transaction.signature_status ?? SignatureStatus.AWAITING) ===
          SignatureStatus.AWAITING
    );
  }

  /**
   * POS-972 On-Screen Signature — Meal Plan.
   * First meal-plan transaction (`meal_plan.transactions[]` — signature fields nest per-transaction
   * there, not flat on the Ticket, see ticket.model.ts's `TicketMealPlan` doc) still awaiting a
   * signature/skip decision. Shared by `checkout.page.ts` and `payments.page.ts` — previously
   * duplicated in both.
   */
  static getPendingMealPlanSignatureTransaction(
    ticket: Ticket
  ): MealPlanTransactionSignature | undefined {
    return ticket?.meal_plan?.transactions?.find(
      (transaction) =>
        transaction.signature_required &&
        (transaction.signature_status ?? SignatureStatus.AWAITING) ===
          SignatureStatus.AWAITING
    );
  }

  static calculateFormattedTakeoutDateTime({
    ticket_date,
    meal_delivery_area_timing_id,
    scheduled_ticket_time,
    order_source
  }: Partial<Ticket>): string {
    const formattedTicketDate = format(
      DateHelper.parse(ticket_date),
      'EEE, MMM dd, yyyy'
    );
    const scheduledTicketTime = meal_delivery_area_timing_id
      ? scheduled_ticket_time
      : 'ASAP';
    const isPosOrderSource = order_source === TicketSource.POS;

    return `${formattedTicketDate} ${
      isPosOrderSource ? '' : '| ' + scheduledTicketTime
    }`;
  }

  static initializeTicket(ticket: Ticket): Ticket {
    ticket.calculated_total = ticket.calculated_total || 0;
    // POS-1798: mealplan_balance_used/mealplan_tender_type aren't always sent flat on the
    // ticket (e.g. the table/checkout endpoint only nests them under meal_plan) — backfill
    // from there before defaulting, matching self-service.selector.ts's existing pattern.
    ticket.mealplan_balance_used =
      ticket.mealplan_balance_used ||
      _.toNumber(ticket.meal_plan?.balance_used) ||
      0;
    ticket.mealplan_tender_type =
      ticket.mealplan_tender_type || ticket.meal_plan?.tender_type;
    ticket.outstanding_balance = ticket.outstanding_balance || 0;
    ticket.pre_tax_subtotal = ticket.pre_tax_subtotal || 0;
    ticket.total_base_price = ticket.total_base_price || 0;
    ticket.total_discounts = ticket.total_discounts || 0;
    ticket.tax_total = ticket.tax_total || 0;
    ticket.ticket_items = ticket.ticket_items || [];
    ticket.can_be_cancelled = true;
    ticket.order_source = ticket.order_source || TicketSource.POS;
    return ticket;
  }

  // POS-1798: mealplan_balance_used/mealplan_tender_type are only reliably populated when
  // TicketHelper.initializeTicket has run on this ticket — some load paths (e.g. the table/
  // checkout endpoint) skip it and only nest the data under meal_plan. Read both directly
  // here instead of depending on load-path-specific normalization having already happened.
  static getMealPlanBalanceUsed(ticket: Ticket): number {
    return (
      ticket?.mealplan_balance_used ||
      _.toNumber(ticket?.meal_plan?.balance_used) ||
      0
    );
  }

  static getMealPlanTenderType(ticket: Ticket): string | undefined {
    return ticket?.mealplan_tender_type || ticket?.meal_plan?.tender_type;
  }

  static isDecliningMealPlanBalanceUsed(ticket: Ticket): boolean {
    return (
      TicketHelper.getMealPlanBalanceUsed(ticket) > 0 &&
      TicketHelper.getMealPlanTenderType(ticket) ===
        MealPlanType.DECLINING_BALANCE
    );
  }

  // POS-1798: Tip Amount is only offered when the ticket is being paid out by a
  // declining-balance meal plan and the location's gratuity_on_meal_plan setting is on.
  // Shared by ticket-actions.component.ts and checkout.page.ts so the two close-ticket
  // entry points can't drift on when the tip prompt shows.
  static shouldPromptForMealPlanTip(
    ticket: Ticket,
    gratuitySettings?: GratuitySettings
  ): boolean {
    return (
      TicketHelper.isDecliningMealPlanBalanceUsed(ticket) &&
      !!gratuitySettings?.gratuity_on_meal_plan
    );
  }

  static getPickupDeliveryFee(params: {
    deliveryType: DeliveryType;
    pickup_delivery_fees: PickupDeliveryFee[];
    isSsk?: boolean;
  }): PickupDeliveryFee {
    const { deliveryType, pickup_delivery_fees, isSsk } = params;
    const PICKUP_DELIVERY_FEE_MAPPER = pickup_delivery_fees.reduce(
      (mapper, fee) => {
        mapper[fee.type] = fee;
        return mapper;
      },
      {} as Record<PickupDeliveryFeeType, PickupDeliveryFee>
    );

    if (isSsk && deliveryType === DeliveryType.TO_GO) {
      return PICKUP_DELIVERY_FEE_MAPPER['pick_up'];
    }

    return PICKUP_DELIVERY_FEE_MAPPER[deliveryType];
  }

  static calculateTicketSubtotal(ticket: Ticket): number {
    const ticketPreTaxSubtotal = +(ticket.pre_tax_subtotal || 0);
    const feePreTaxSubtotal = _.sum(
      ticket.local_attributes?.ticketItemFees?.map(
        (feeItem) => feeItem.pre_tax_subtotal || 0
      )
    );
    return _.round(_.subtract(ticketPreTaxSubtotal, feePreTaxSubtotal), 4);
  }

  static calculateTicketDiscountApplied(ticket: Ticket): number {
    const ticketDiscountAmount = +ticket.total_discounts || 0;
    const feeItemDiscountAmount = _.sum(
      ticket.local_attributes?.ticketItemFees?.map(
        (feeItem) => +feeItem.discount_amount || 0
      )
    );

    return _.round(_.subtract(ticketDiscountAmount, feeItemDiscountAmount), 4);
  }
}
